import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Briefcase, Check, Filter, Hourglass, ListPlus, Repeat2, Smile, Sparkles, Target } from "lucide-react";
import { Badge, Button, Dialog, Spinner, Textarea, Tooltip } from "@/components/ui";
import { Blog, BlogRewriteArticleContext, BlogServices } from "@/services/bedrock";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { blogBlockText, blogSectionText, normalizeBlogSection, parseTagInput } from "../blogBody";

export type BlogRewriteField = "title" | "subtitle" | "excerpt" | "tags" | "searchDescription";
export type BlogRewriteScope = "section" | "heading" | "body" | "list" | "pullQuote" | BlogRewriteField;

export const rewriteApiField = (
  field: BlogRewriteField
): "title" | "subtitle" | "excerpt" | "tags" | "metaDescription" =>
  field === "searchDescription" ? "metaDescription" : field;

export type BlogRewriteResult =
  | { kind: "section"; section: Blog.Section }
  | { kind: "field"; field: BlogRewriteField; value: string | string[] };

type Props = {
  open: boolean;
  blogId: number;
  scope: BlogRewriteScope;
  previousSection?: Blog.Section;
  blockIndex?: number;
  previousValue?: string | string[];
  articleContext?: BlogRewriteArticleContext;
  onRewritten: (result: BlogRewriteResult) => void;
  onReviewing?: (value: string | string[] | Blog.Section | null) => void;
  onDismiss?: () => void;
};

const FIELD_SCOPES: BlogRewriteField[] = ["title", "subtitle", "excerpt", "tags", "searchDescription"];
const isFieldScope = (scope: BlogRewriteScope): scope is BlogRewriteField =>
  FIELD_SCOPES.includes(scope as BlogRewriteField);

type Suggestion = { label: string; prompt: string; icon: React.ElementType };
type ChipId = "shorter" | "longer" | "simpler" | "professional" | "friendlier" | "rephrase" | "specific";

type ScopeUi = {
  title: string;
  apply: string;
  success: string;
  current: string;
  suggested: string;
  intro: string;
  compareTitle: string;
  compareDescription: string;
  noteLabel: string;
  placeholder: string;
  defaultInstructions?: string;
};

const SUBJECT: Record<BlogRewriteScope, string> = {
  section: "this section",
  heading: "this heading",
  body: "this body copy",
  list: "this list",
  pullQuote: "this pull quote",
  title: "the title",
  subtitle: "the subtitle",
  excerpt: "the excerpt",
  tags: "the tags",
  searchDescription: "the search description",
};

const CORE_CHIPS: Record<ChipId, { label: string; icon: React.ElementType; prompt: (subject: string) => string }> = {
  shorter: {
    label: "Shorter",
    icon: Hourglass,
    prompt: subject => `Make ${subject} shorter and easier to scan. Keep the meaning.`,
  },
  longer: {
    label: "Longer",
    icon: ListPlus,
    prompt: subject => `Expand ${subject} with a bit more helpful detail. Do not invent facts, stats, or sources.`,
  },
  simpler: {
    label: "Simpler",
    icon: Filter,
    prompt: subject => `Rewrite ${subject} in simpler, clearer language. Keep the meaning.`,
  },
  professional: {
    label: "More professional",
    icon: Briefcase,
    prompt: subject =>
      `Rewrite ${subject} in a more professional, polished tone. Keep it first person and credible, not stiff or salesy.`,
  },
  friendlier: {
    label: "Friendlier",
    icon: Smile,
    prompt: subject => `Rewrite ${subject} in a friendlier, more conversational tone.`,
  },
  rephrase: {
    label: "Rephrase",
    icon: Repeat2,
    prompt: subject => `Rephrase ${subject} with different wording. Keep the same meaning and facts.`,
  },
  specific: {
    label: "More specific",
    icon: Target,
    prompt: subject => `Make ${subject} more specific and concrete. Do not invent facts.`,
  },
};

const CHIPS_BY_SCOPE: Record<BlogRewriteScope, ChipId[]> = {
  title: ["shorter", "specific", "simpler", "professional", "friendlier", "rephrase"],
  subtitle: ["shorter", "specific", "simpler", "friendlier", "rephrase"],
  excerpt: ["shorter", "longer", "simpler", "friendlier", "rephrase"],
  searchDescription: ["shorter", "simpler", "professional", "rephrase"],
  tags: ["shorter", "longer", "specific", "rephrase"],
  heading: ["shorter", "specific", "simpler", "friendlier", "rephrase"],
  body: ["shorter", "longer", "simpler", "professional", "friendlier", "rephrase"],
  section: ["shorter", "longer", "simpler", "professional", "friendlier", "rephrase"],
  list: ["shorter", "longer", "simpler", "rephrase"],
  pullQuote: ["shorter", "simpler", "friendlier", "rephrase"],
};

const LABEL_OVERRIDES: Partial<Record<BlogRewriteScope, Partial<Record<ChipId, string>>>> = {
  tags: { shorter: "Fewer tags", longer: "More tags" },
  list: { shorter: "Fewer items", longer: "More items" },
  searchDescription: { simpler: "Clearer" },
};

const CHIP_CONSTRAINTS: Partial<Record<BlogRewriteScope, Partial<Record<ChipId, string>>>> = {
  title: {
    shorter: "Keep it a scannable headline, not a sentence.",
    specific: "Name the concrete topic instead of a generic headline. Do not turn it into clickbait.",
    simpler: "Use plain words a client would understand at a glance.",
  },
  subtitle: {
    shorter: "Keep it one brief hook under the title, not a second headline.",
    specific: "Make it more specific to what the article actually covers.",
  },
  heading: {
    shorter: "Keep it short enough to scan as a section heading.",
    specific: "Make it more specific to the copy that follows.",
  },
  pullQuote: {
    shorter: "Keep it one memorable line, not a paragraph.",
    friendlier: "Keep it personal, still one short line.",
  },
  excerpt: {
    shorter: "Stay around one sentence for a listing card.",
    longer: "Stay around one or two sentences for a listing card.",
  },
  searchDescription: {
    shorter: "Keep it a single sentence well under 160 characters.",
    simpler: "A stranger should understand the page from this snippet alone. Stay under 160 characters.",
    professional: "Keep it a single search-result sentence under 160 characters.",
    rephrase: "Stay under 160 characters as a single search-result sentence.",
  },
  tags: {
    shorter: "Keep only the strongest topics from the article.",
    longer: "Add a couple more topical tags from the article. Stay in the 3 to 6 range. Do not invent topics.",
    specific: "Use concrete topics from the article instead of generic ones. Do not invent topics.",
    rephrase: "Use different wording for the same topics. Do not invent new topics.",
  },
  list: {
    shorter: "Keep each item short and parallel. Cut weaker items if needed.",
    longer: "Add a couple more useful items only if they are already grounded in the article.",
    simpler: "Keep each item short, parallel, and easy to scan.",
    rephrase: "Keep the same points. Reword the items so they stay parallel.",
  },
  body: {
    longer: "Add helpful detail only if it is already in the draft, notes, or transcript. Do not invent facts.",
  },
  section: {
    longer: "Add helpful detail only if it is already in the draft, notes, or transcript. Do not invent facts.",
  },
};

const suggestionsFor = (scope: BlogRewriteScope): Suggestion[] => {
  const subject = SUBJECT[scope];
  const constraints = CHIP_CONSTRAINTS[scope] ?? {};
  const labels = LABEL_OVERRIDES[scope] ?? {};
  return CHIPS_BY_SCOPE[scope].map(id => {
    const chip = CORE_CHIPS[id];
    return {
      label: labels[id] ?? chip.label,
      icon: chip.icon,
      prompt: [chip.prompt(subject), constraints[id]].filter(Boolean).join(" "),
    };
  });
};

const ARTICLE_REWRITE_UI = {
  intro: "Choose how to rewrite this. Nothing is saved until you accept.",
  compareTitle: "Compare this rewrite",
  compareDescription: "The current copy stays in the editor. The preview shows the suggestion until you accept it.",
  noteLabel: "Instructions or extra context",
  placeholder: "Add extra direction, a phrase to include, or a tone to avoid…",
} satisfies Pick<ScopeUi, "intro" | "compareTitle" | "compareDescription" | "noteLabel" | "placeholder">;

const SCOPE_UI: Record<BlogRewriteScope, ScopeUi> = {
  section: {
    title: "Rewrite this section",
    apply: "Rewrite section",
    success: "Section updated. Save the blog when you are happy with it.",
    current: "Current section",
    suggested: "Suggested section",
    ...ARTICLE_REWRITE_UI,
    intro: "Choose how to rewrite this section. Nothing is saved until you accept.",
    compareTitle: "Compare these sections",
  },
  heading: {
    title: "Rewrite this heading",
    apply: "Rewrite heading",
    success: "Heading updated. Save the blog when you are happy with it.",
    current: "Current heading",
    suggested: "Suggested heading",
    intro: "Choose how to rewrite this heading.",
    compareTitle: "Compare these headings",
    compareDescription:
      "The current heading stays in the editor. The preview shows the suggestion until you accept it.",
    noteLabel: "Optional note",
    placeholder: "Optional: a phrase to include…",
    defaultInstructions: "Suggest a clearer, more specific section heading that matches the copy that follows.",
  },
  body: {
    title: "Rewrite this body",
    apply: "Rewrite body",
    success: "Body updated. Save the blog when you are happy with it.",
    current: "Current body",
    suggested: "Suggested body",
    intro: "Choose how to rewrite this body copy.",
    compareTitle: "Compare this body",
    compareDescription: "The current body stays in the editor. The preview shows the suggestion until you accept it.",
    noteLabel: "Instructions or extra context",
    placeholder: "Add extra direction, a phrase to include, or a tone to avoid…",
  },
  list: {
    title: "Rewrite this list",
    apply: "Rewrite list",
    success: "List updated. Save the blog when you are happy with it.",
    current: "Current list",
    suggested: "Suggested list",
    intro: "Choose how to rewrite this list.",
    compareTitle: "Compare these lists",
    compareDescription: "The current list stays in the editor. The preview shows the suggestion until you accept it.",
    noteLabel: "Optional note",
    placeholder: "Optional: an item to include or avoid…",
    defaultInstructions: "Rewrite this list so each item is short, parallel, and grounded in the article.",
  },
  pullQuote: {
    title: "Rewrite this quote",
    apply: "Rewrite quote",
    success: "Quote updated. Save the blog when you are happy with it.",
    current: "Current quote",
    suggested: "Suggested quote",
    intro: "Choose how to rewrite this pull quote.",
    compareTitle: "Compare these quotes",
    compareDescription: "The current quote stays in the editor. The preview shows the suggestion until you accept it.",
    noteLabel: "Optional note",
    placeholder: "Optional: a phrase to include…",
    defaultInstructions: "Suggest a short, memorable pull quote grounded in the surrounding section.",
  },
  title: {
    title: "Suggest a title",
    apply: "Suggest title",
    success: "Title updated. Save the blog when you are happy with it.",
    current: "Current title",
    suggested: "Suggested title",
    intro: "Choose how to rewrite the title.",
    compareTitle: "Compare these titles",
    compareDescription: "The current title stays in the editor. The preview shows the suggestion until you accept it.",
    noteLabel: "Optional note",
    placeholder: "Optional: a phrase to include, or a tone to avoid…",
    defaultInstructions:
      "Suggest a clearer, more specific title from the article. Keep it scannable and free of clickbait.",
  },
  subtitle: {
    title: "Suggest a subtitle",
    apply: "Suggest subtitle",
    success: "Subtitle updated. Save the blog when you are happy with it.",
    current: "Current subtitle",
    suggested: "Suggested subtitle",
    intro: "Choose how to rewrite the subtitle.",
    compareTitle: "Compare these subtitles",
    compareDescription:
      "The current subtitle stays in the editor. The preview shows the suggestion until you accept it.",
    noteLabel: "Optional note",
    placeholder: "Optional: a phrase to include, or a tone to avoid…",
    defaultInstructions: "Suggest a short supporting hook for the title, grounded in the article.",
  },
  excerpt: {
    title: "Suggest an excerpt",
    apply: "Suggest excerpt",
    success: "Excerpt updated. Save the blog when you are happy with it.",
    current: "Current excerpt",
    suggested: "Suggested excerpt",
    intro: "Choose how to rewrite the excerpt.",
    compareTitle: "Compare these excerpts",
    compareDescription:
      "The current excerpt stays in the editor. The preview shows the suggestion until you accept it.",
    noteLabel: "Optional note",
    placeholder: "Optional: a takeaway to include…",
    defaultInstructions: "Suggest a one- or two-sentence excerpt for cards and listings, grounded in the article.",
  },
  tags: {
    title: "Suggest tags",
    apply: "Suggest tags",
    success: "Tags updated. Save the blog when you are happy with them.",
    current: "Current tags",
    suggested: "Suggested tags",
    intro: "Choose how to rewrite the tags.",
    compareTitle: "Compare these tags",
    compareDescription: "The current tags stay in the editor. The preview shows the suggestion until you accept it.",
    noteLabel: "Optional note",
    placeholder: "Optional: a topic to include, or tags to avoid…",
    defaultInstructions:
      "Suggest 3 to 6 short topical tags from the article in Title Case. Do not invent topics that are not in the article.",
  },
  searchDescription: {
    title: "Suggest a search description",
    apply: "Suggest description",
    success: "Search description updated. Save the blog when you are happy with it.",
    current: "Current search description",
    suggested: "Suggested search description",
    intro: "Choose how to rewrite the search description.",
    compareTitle: "Compare these descriptions",
    compareDescription: "The current search description stays in the editor. Accept it to use the suggestion.",
    noteLabel: "Optional note",
    placeholder: "Optional: a phrase or topic to include…",
    defaultInstructions:
      "Write a single sentence under 160 characters for search results, grounded in the article. Do not invent claims.",
  },
};

const displayValue = (value: string | string[] | undefined): string =>
  Array.isArray(value) ? value.filter(Boolean).join(", ") : value ?? "";

const compareText = (
  scope: BlogRewriteScope,
  previousSection: Blog.Section | undefined,
  blockIndex: number | undefined,
  previousValue: string | string[] | undefined
): string => {
  if (isFieldScope(scope)) return displayValue(previousValue);
  if (!previousSection) return "";
  if (blockIndex == null) return blogSectionText(previousSection);
  return blogBlockText(previousSection.blocks?.[blockIndex]);
};

export const BlogRewritePanel: React.FC<Props> = ({
  open,
  blogId,
  scope,
  previousSection,
  blockIndex,
  previousValue,
  articleContext,
  onRewritten,
  onReviewing,
  onDismiss,
}) => {
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null);
  const [customInstructions, setCustomInstructions] = useState("");
  const [rewriting, setRewriting] = useState(false);
  const [proposal, setProposal] = useState<Blog.Section | string | string[] | null>(null);
  const ui = SCOPE_UI[scope];
  const isTags = scope === "tags";
  const suggestions = suggestionsFor(scope);
  const onReviewingRef = useRef(onReviewing);
  onReviewingRef.current = onReviewing;

  useEffect(() => {
    onReviewingRef.current?.(open ? proposal : null);
  }, [open, proposal]);

  useEffect(() => {
    return () => onReviewingRef.current?.(null);
  }, []);

  useEffect(() => {
    if (open) return;
    setProposal(null);
    setSelectedSuggestion(null);
    setCustomInstructions("");
    setRewriting(false);
  }, [open]);

  const toggleSuggestion = (label: string) => {
    setSelectedSuggestion(current => (current === label ? null : label));
  };

  const handleRewrite = async () => {
    const combinedInstructions = [
      ...suggestions.filter(suggestion => suggestion.label === selectedSuggestion).map(suggestion => suggestion.prompt),
      customInstructions.trim(),
    ]
      .filter(Boolean)
      .join(" ");

    if (!combinedInstructions && !ui.defaultInstructions) {
      toast.error("Choose a suggestion or add instructions first.");
      return;
    }

    setRewriting(true);
    try {
      const result = await BlogServices.rewriteBlog(blogId, {
        userInstructions: combinedInstructions || ui.defaultInstructions || "",
        articleContext,
        ...(isFieldScope(scope)
          ? {
              field: rewriteApiField(scope),
              previousValue,
            }
          : { previousSection: previousSection!, blockIndex }),
      });
      if (isFieldScope(scope)) {
        if (result?.value == null) {
          toast.error("We could not rewrite that part. Try different instructions.");
          return;
        }
        setProposal(result.value);
        return;
      }
      const section = result?.section ? normalizeBlogSection(result.section) : null;
      if (!section) {
        toast.error("We could not rewrite that part. Try different instructions.");
        return;
      }
      setProposal(section);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || "An error occurred while rewriting.");
    } finally {
      setRewriting(false);
    }
  };

  const handleAccept = () => {
    if (proposal == null) return;
    if (isFieldScope(scope)) {
      onRewritten({ kind: "field", field: scope, value: proposal as string | string[] });
    } else {
      onRewritten({ kind: "section", section: proposal as Blog.Section });
    }
    onReviewing?.(null);
    toast.success(ui.success);
    onDismiss?.();
  };

  const suggestedText = isFieldScope(scope)
    ? displayValue(proposal as string | string[] | undefined)
    : proposal && typeof proposal === "object" && !Array.isArray(proposal)
    ? compareText(scope, proposal, blockIndex, undefined)
    : "";

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen && !rewriting) onDismiss?.();
      }}
      size={proposal ? "xl" : "lg"}
      showCloseButton={!rewriting}
      autoFocus
    >
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2">
          {proposal ? ui.compareTitle : ui.title}
          <img alt="SocialCoach AI" src="https://cdn.socialcoach.io/socoai_mono_positive.svg" className="h-[12px]" />
        </Dialog.Title>
        <Dialog.Description>{proposal ? ui.compareDescription : ui.intro}</Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="relative flex flex-col gap-4">
        {rewriting && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg bg-white/70 backdrop-blur-[1px]">
            <Spinner className="mb-2 size-6 animate-spin text-primary" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Rewriting…</span>
          </div>
        )}

        {proposal != null ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {isTags ? (
              <>
                <CompareTags label={ui.current} tags={parseTagInput(previousValue)} tone="current" />
                <CompareTags
                  label={ui.suggested}
                  tags={parseTagInput(proposal as string | string[])}
                  tone="suggested"
                />
              </>
            ) : (
              <>
                <CompareColumn
                  label={ui.current}
                  text={compareText(scope, previousSection, blockIndex, previousValue)}
                  tone="current"
                />
                <CompareColumn label={ui.suggested} text={suggestedText} tone="suggested" />
              </>
            )}
          </div>
        ) : (
          <>
            <div
              className={suggestions.length <= 4 ? "grid grid-cols-2 gap-2" : "grid grid-cols-2 gap-2 sm:grid-cols-3"}
            >
              {suggestions.map(suggestion => {
                const selected = selectedSuggestion === suggestion.label;
                const Icon = suggestion.icon;
                return (
                  <button
                    key={suggestion.label}
                    type="button"
                    disabled={rewriting}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => toggleSuggestion(suggestion.label)}
                    className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-2 text-left text-xs font-bold leading-tight transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 ${
                      selected
                        ? "border-indigo-200 bg-indigo-50 text-indigo-600"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600"
                    }`}
                  >
                    <Icon className="size-3.5 shrink-0" />
                    <span>{suggestion.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-slate-500">{ui.noteLabel}</span>
              <Textarea
                rows={3}
                value={customInstructions}
                disabled={rewriting}
                placeholder={ui.placeholder}
                className="bg-white text-sm"
                onKeyDown={event => {
                  if (event.key === " " || event.code === "Space") event.stopPropagation();
                }}
                onChange={event => setCustomInstructions(event.target.value)}
              />
            </div>
          </>
        )}
      </Dialog.Content>

      <Dialog.Footer>
        {proposal != null ? (
          <>
            <Button type="button" variant="outline" onClick={onDismiss}>
              Keep original
            </Button>
            <Button type="button" variant="outline" onClick={() => setProposal(null)}>
              Try again
            </Button>
            <Button type="button" onClick={handleAccept}>
              <Check className="size-4" />
              Accept
            </Button>
          </>
        ) : (
          <Button
            type="button"
            disabled={rewriting || (!ui.defaultInstructions && !customInstructions.trim() && !selectedSuggestion)}
            onClick={handleRewrite}
          >
            <Sparkles className="size-4" />
            {rewriting ? "Rewriting…" : ui.apply}
          </Button>
        )}
      </Dialog.Footer>
    </Dialog>
  );
};

const ComparePane: React.FC<{
  label: string;
  tone: "current" | "suggested";
  className?: string;
  children: React.ReactNode;
}> = ({ label, tone, className, children }) => (
  <div
    className={cn(
      "flex min-h-24 flex-col rounded-lg border p-3",
      tone === "suggested" ? "border-indigo-200 bg-indigo-50/70" : "border-slate-200 bg-slate-50",
      className
    )}
  >
    <span
      className={cn(
        "text-[11px] font-bold uppercase tracking-wide",
        tone === "suggested" ? "text-indigo-700" : "text-slate-500"
      )}
    >
      {label}
    </span>
    {children}
  </div>
);

const CompareTags: React.FC<{ label: string; tags: string[]; tone: "current" | "suggested" }> = ({
  label,
  tags,
  tone,
}) => (
  <ComparePane label={label} tone={tone} className="gap-2">
    {tags.length === 0 ? (
      <p className="text-sm text-slate-500">None</p>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {tags.map(tag => (
          <Badge key={tag} variant="outline" className="text-sm px-2 py-0.5">
            {tag}
          </Badge>
        ))}
      </div>
    )}
  </ComparePane>
);

const CompareColumn: React.FC<{ label: string; text: string; tone: "current" | "suggested" }> = ({
  label,
  text,
  tone,
}) => (
  <ComparePane label={label} tone={tone} className="gap-1.5">
    <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{text || "Empty"}</p>
  </ComparePane>
);

const RewriteHoverContext = createContext(false);

const isInside = (node: HTMLElement | null, target: EventTarget | null) =>
  !!(node && target instanceof Node && node.contains(target));

export const RewriteHoverRegion: React.FC<React.ComponentProps<"div">> = ({
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  children,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!hovered) return;
    const node = ref.current;
    if (!node) return;

    const syncHover = (event: PointerEvent) => {
      const under = document.elementFromPoint(event.clientX, event.clientY);
      if (!under || !node.contains(under)) setHovered(false);
    };

    document.addEventListener("pointermove", syncHover);
    return () => document.removeEventListener("pointermove", syncHover);
  }, [hovered]);

  return (
    <RewriteHoverContext.Provider value={hovered || focused}>
      <div
        {...props}
        ref={ref}
        onMouseEnter={event => {
          setHovered(true);
          onMouseEnter?.(event);
        }}
        onMouseLeave={event => {
          if (isInside(ref.current, event.relatedTarget)) return;
          setHovered(false);
          onMouseLeave?.(event);
        }}
        onFocus={event => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={event => {
          if (isInside(ref.current, event.relatedTarget)) return;
          setFocused(false);
          onBlur?.(event);
        }}
      >
        {children}
      </div>
    </RewriteHoverContext.Provider>
  );
};

export const RewriteHoverButton: React.FC<{
  label: string;
  active?: boolean;
  forceVisible?: boolean;
  onClick: () => void;
}> = ({ label, active, forceVisible, onClick }) => {
  const hovered = useContext(RewriteHoverContext);
  const visible = !!(active || forceVisible || hovered);

  return (
    <span className="inline-flex" style={{ opacity: visible ? 1 : 0 }} aria-hidden={!visible}>
      <Tooltip>
        <Tooltip.Trigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              tabIndex={visible ? 0 : -1}
              className={cn("size-7 text-slate-400", active && "text-indigo-600")}
              aria-label={label}
              aria-pressed={active}
              onMouseDown={event => event.preventDefault()}
              onClick={event => {
                event.stopPropagation();
                onClick();
              }}
            >
              <Sparkles className="size-4" />
            </Button>
          }
        />
        {visible && <Tooltip.Content>{label}</Tooltip.Content>}
      </Tooltip>
    </span>
  );
};
